// pages/Company/CompanySettingsPage.jsx
// ─────────────────────────────────────────────────────────────
// UPDATED — added the "Delete Company" danger zone at the bottom of
// the page. Owner-only (matches the backend's requireCompanyRole
// ("owner") gate on DELETE /companies/:companyId — this page hides
// the section entirely for non-owners, since even SEEING a delete
// button for an action you can't perform is just noise/confusion).
//
// FRICTION BY DESIGN — this is a genuinely irreversible action
// (hard delete: company + all staff + all members, no recovery path
// per the backend implementation). The confirmation flow requires:
//   1. Typing the exact company name to confirm intent — the standard
//      pattern for destructive actions (GitHub repo deletion, etc.)
//      that a generic "Are you sure?" dialog doesn't provide, since
//      those are too easy to click through on autopilot.
//   2. Entering the transaction PIN — re-authentication for the
//      action itself, matching how PayrollRunPage.jsx gates its own
//      high-value action (running payroll) the same way.
// Both steps must pass before the delete request is even sent.
//
// WALLET BALANCE PRECONDITION is enforced server-side (see
// deleteCompany in companyController.js) — this page surfaces that
// requirement proactively if a balance is known, so the owner isn't
// surprised by a rejection after going through the whole confirm flow,
// but the actual block is the backend's, not this client-side check.
// ─────────────────────────────────────────────────────────────
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast }                from "react-toastify";
import { apiClient }            from "../../api/apiServices";

const FIELDS = [
  ["name", "Company name", "text"],
  ["rcNumber", "RC number (CAC)", "text"],
  ["industry", "Industry", "text"],
  ["address", "Address", "text"],
  ["website", "Website", "text"],
  ["contactName", "Contact name", "text"],
  ["contactEmail", "Contact email", "email"],
  ["contactPhone", "Contact phone", "tel"],
  ["lowBalanceThreshold", "Low-balance alert threshold (₦)", "number"],
];

const COMPLETENESS_FIELDS = ["rcNumber", "industry", "address", "website", "contactName", "contactEmail", "contactPhone"];

function ProfileCompletionCard({ company, onDismiss }) {
  const filledCount = COMPLETENESS_FIELDS.filter(k => company[k] && String(company[k]).trim()).length;
  const total = COMPLETENESS_FIELDS.length;
  const pct = Math.round((filledCount / total) * 100);

  if (pct === 100) return null;

  return (
    <div className="bg-brand-light border border-brand-border rounded-2xl p-4 mb-4 relative">
      <button onClick={onDismiss} className="absolute top-3 right-3 text-brand-text/50 hover:text-brand-text">
        <i className="ri-close-line text-sm" />
      </button>
      <div className="flex items-center gap-2 mb-2">
        <i className="ri-sparkling-2-line text-brand" />
        <p className="text-brand-text font-semibold text-sm">Complete your company profile</p>
      </div>
      <div className="flex items-center gap-2 mb-1.5">
        <div className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden">
          <div className="h-full bg-brand rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-brand-text text-xs font-bold flex-shrink-0">{pct}%</span>
      </div>
      <p className="text-brand-text text-xs opacity-80">
        {total - filledCount} more field{total - filledCount !== 1 ? "s" : ""} to go — fill them in below.
      </p>
    </div>
  );
}

// FIXED — new. The destructive confirmation flow, isolated as its own
// component for readability since it has real internal state
// (confirm-text, pin, step) separate from the settings form above it.
function DeleteCompanyZone({ companyId, companyName, walletBalance, onDeleted }) {
  const [expanded, setExpanded]   = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [pin, setPin]             = useState("");
  const [deleting, setDeleting]   = useState(false);

  const nameMatches = confirmText.trim() === companyName;
  const hasBalance = Number(walletBalance) > 0;

  const handleDelete = async () => {
    if (!nameMatches || pin.length !== 4) return;
    setDeleting(true);
    try {
      const r = await apiClient.delete(`/mtrade/companies/${companyId}`, {
        data: { transactionPin: pin }, // axios DELETE body goes under `data`
      });
      if (r.data.status) {
        toast.success(r.data.message || "Company deleted");
        onDeleted();
      } else {
        toast.error(r.data.message);
        setPin("");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not delete company");
      setPin("");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="mt-8 border-2 border-danger-border rounded-2xl overflow-hidden">
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-4 py-3.5 bg-danger-light"
      >
        <div className="flex items-center gap-2">
          <i className="ri-error-warning-line text-danger" />
          <span className="text-danger-text font-semibold text-sm">Danger Zone</span>
        </div>
        <i className={`ri-arrow-down-s-line text-danger transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>

      {expanded && (
        <div className="p-4 space-y-4 bg-surface">
          <div>
            <p className="text-primary font-semibold text-sm mb-1">Delete this company</p>
            <p className="text-muted text-xs leading-relaxed">
              This permanently deletes <strong className="text-primary">{companyName}</strong>, every staff record, and every team member's access. This cannot be undone — there is no way to recover this data afterward.
            </p>
          </div>

          {hasBalance ? (
            <div className="flex items-start gap-2 px-3 py-2.5 bg-warning-light border border-warning-border rounded-xl">
              <i className="ri-wallet-3-line text-sm mt-0.5 flex-shrink-0" style={{ color: "var(--color-warning)" }} />
              <p className="text-xs" style={{ color: "var(--color-warning-text)" }}>
                The company wallet still has ₦{Number(walletBalance).toLocaleString()}. You need to withdraw or use up this balance before the company can be deleted.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="text-secondary text-xs font-semibold mb-1.5 block">
                  Type <span className="font-mono text-danger-text">{companyName}</span> to confirm
                </label>
                <input
                  value={confirmText}
                  onChange={e => setConfirmText(e.target.value)}
                  placeholder={companyName}
                  className="w-full bg-surface-muted border-2 border-border-strong rounded-xl px-3 py-2.5 text-sm text-primary outline-none focus:border-danger placeholder:text-faint"
                />
              </div>

              {nameMatches && (
                <div>
                  <label className="text-secondary text-xs font-semibold mb-1.5 block">Enter your transaction PIN to confirm</label>
                  <input
                    type="password" inputMode="numeric" maxLength={4} value={pin}
                    onChange={e => setPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="••••"
                    className="w-full bg-surface-muted border-2 border-border-strong rounded-xl px-3 py-3 text-center tracking-[0.5em] text-lg outline-none focus:border-danger text-primary"
                  />
                </div>
              )}

              <button
                onClick={handleDelete}
                disabled={!nameMatches || pin.length !== 4 || deleting}
                className="w-full py-3 rounded-xl text-sm font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                style={{ background: "var(--color-danger)", color: "#fff" }}
              >
                {deleting ? "Deleting…" : "Permanently Delete Company"}
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default function CompanySettingsPage() {
  const { companyId } = useParams();
  const navigate = useNavigate();

  const [company, setCompany] = useState(null);
  const [wallet, setWallet]   = useState(null);
  const [form, setForm]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [dirty, setDirty]     = useState(false);
  const [showNudge, setShowNudge] = useState(
    () => sessionStorage.getItem(`hideProfileNudge_${companyId}`) !== "true"
  );

  useEffect(() => {
    Promise.all([
      apiClient.get(`/mtrade/companies/${companyId}`),
      apiClient.get(`/mtrade/companies/${companyId}/wallet`).catch(() => null),
    ]).then(([companyRes, walletRes]) => {
      if (companyRes.data.status) {
        setCompany(companyRes.data.data);
        setForm({
          name: companyRes.data.data.name || "",
          rcNumber: companyRes.data.data.rcNumber || "",
          industry: companyRes.data.data.industry || "",
          address: companyRes.data.data.address || "",
          website: companyRes.data.data.website || "",
          contactName: companyRes.data.data.contactName || "",
          contactEmail: companyRes.data.data.contactEmail || "",
          contactPhone: companyRes.data.data.contactPhone || "",
          lowBalanceThreshold: companyRes.data.data.lowBalanceThreshold ?? "",
        });
      }
      if (walletRes?.data?.status) setWallet(walletRes.data.data);
    })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [companyId]);

  const canEdit   = company?.myRole === "owner" || company?.myRole === "admin";
  const isOwner   = company?.myRole === "owner";

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setDirty(true); };

  const dismissNudge = () => {
    setShowNudge(false);
    sessionStorage.setItem(`hideProfileNudge_${companyId}`, "true");
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      const r = await apiClient.patch(`/mtrade/companies/${companyId}`, {
        ...form,
        lowBalanceThreshold: Number(form.lowBalanceThreshold) || 0,
      });
      if (r.data.status) {
        toast.success("Settings saved");
        setCompany(prev => ({ ...prev, ...r.data.data }));
        setDirty(false);
      } else toast.error(r.data.message);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save settings");
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-bg flex items-center justify-center">
      <div className="w-10 h-10 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: "var(--color-brand)", borderTopColor: "transparent" }} />
    </div>
  );

  if (!company) return (
    <div className="min-h-screen bg-bg flex flex-col items-center justify-center px-6 text-center font-display">
      <i className="ri-error-warning-line text-4xl text-faint mb-3" />
      <p className="text-secondary text-sm">Company not found.</p>
      <button onClick={() => navigate("/business")} className="mt-4 text-brand text-sm font-semibold">Back to Dashboard</button>
    </div>
  );

  return (
    <div className="min-h-screen bg-bg pb-28 font-display">
      <div className="bg-surface px-5 pt-12 pb-4 border-b border-border">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <div className="flex items-center gap-3 min-w-0">
            <button onClick={() => navigate(-1)} className="w-9 h-9 rounded-xl bg-surface-hover flex items-center justify-center flex-shrink-0">
              <i className="ri-arrow-left-line text-secondary text-lg" />
            </button>
            <div className="min-w-0">
              <h1 className="text-primary font-bold text-lg truncate">Company Settings</h1>
              <p className="text-muted text-xs truncate">{company.name}</p>
            </div>
          </div>
          {dirty && canEdit && (
            <button onClick={save} disabled={saving} className="text-xs font-bold px-3 py-2 rounded-xl bg-brand text-on-brand disabled:opacity-60 flex-shrink-0">
              {saving ? "Saving…" : "Save"}
            </button>
          )}
        </div>
      </div>

      <div className="px-5 pt-5 pb-10 max-w-lg mx-auto space-y-3">
        {canEdit && showNudge && (
          <ProfileCompletionCard company={company} onDismiss={dismissNudge} />
        )}

        {!canEdit && (
          <div className="bg-warning-light border border-warning-border rounded-2xl px-4 py-3 flex items-start gap-2.5">
            <i className="ri-lock-line text-warning flex-shrink-0 mt-0.5" />
            <p className="text-warning-text text-xs leading-relaxed">
              Only the owner or an admin can edit these details. You can view them, but not make changes.
            </p>
          </div>
        )}

        {FIELDS.map(([k, label, type]) => (
          <div key={k}>
            <label className="text-secondary text-sm font-semibold mb-1.5 block">{label}</label>
            <input
              type={type}
              value={form[k]}
              onChange={e => set(k, e.target.value)}
              disabled={!canEdit}
              className="w-full bg-surface border-2 border-border-strong rounded-2xl px-4 py-3 text-sm text-primary outline-none focus:border-brand placeholder:text-faint disabled:opacity-60 disabled:cursor-not-allowed"
            />
          </div>
        ))}

        {canEdit && (
          <button
            onClick={save}
            disabled={saving || !dirty}
            className="w-full mt-2 bg-brand hover:bg-brand-hover disabled:bg-surface-hover disabled:text-faint text-on-brand py-4 rounded-2xl font-bold text-sm transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            {saving ? <div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : "Save Changes"}
          </button>
        )}

        {/* FIXED — new. Owner-only, hidden entirely for everyone else. */}
        {isOwner && (
          <DeleteCompanyZone
            companyId={companyId}
            companyName={company.name}
            walletBalance={wallet?.balance ?? 0}
            onDeleted={() => navigate("/business")}
          />
        )}
      </div>
    </div>
  );
}
